import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table, Spinner, Alert } from 'react-bootstrap';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ExpandableDashboardRow from '../components/boards/rows/DashboardTableRow';
import { fetchDashboardData } from '../features/dashboardSlice';
import UnitFilter from '../components/filter/UnitFilter';
import { selectUnits, selectUnitsStatus } from '../features/unitsSlice';
import { selectUserData } from '../features/userSlice';
import {
    addActionAreaCheckpointStatusCount,
    addMeasureCheckpointStatusCount,
    createActionAreaCheckpointCounts,
    createMeasureCheckpointCounts,
} from '../utils/checkpointStatus';
import './Dashboard.css';

/**
 * Custom hook to detect screen width and determine mobile/tablet breakpoints
 * Mobile: < 768px (show legend below chart, no inline labels)
 * Tablet: 768px - 992px (smaller inline labels)
 * Desktop: >= 992px (full inline labels)
 */
const useResponsiveChart = () => {
    const getInitialState = () => {
        if (typeof window === 'undefined') {
            return { isMobile: false, isTablet: false, width: 1200 };
        }
        const w = window.innerWidth;
        return {
            isMobile: w < 768,
            isTablet: w >= 768 && w < 992,
            width: w
        };
    };

    const [dimensions, setDimensions] = useState(getInitialState);

    useEffect(() => {
        let timeoutId = null;
        
        const handleResize = () => {
            // Debounce resize events for performance
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const w = window.innerWidth;
                setDimensions({
                    isMobile: w < 768,
                    isTablet: w >= 768 && w < 992,
                    width: w
                });
            }, 100);
        };

        window.addEventListener('resize', handleResize);
        // Ensure correct state on mount (handles SSR hydration)
        handleResize();
        
        return () => {
            window.removeEventListener('resize', handleResize);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    return dimensions;
};

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Access units from unitsSlice to check dashboard access permission
    const units = useSelector(selectUnits);
    const unitsStatus = useSelector(selectUnitsStatus);
    const userData = useSelector(selectUserData);
    
    const dashboardData = useSelector((state) => state.dashboard.data);
    const dashboardStatus = useSelector((state) => state.dashboard.status);
    const dashboardError = useSelector((state) => state.dashboard.error);
    const checkpoints = useSelector((state) => state.dashboard.checkpoints || []);
    const defaultQuestions = useSelector((state) => state.dashboard.defaultQuestions || []);
    
    // Check if user has access to dashboard (same logic as Header.js)
    const hasDashboardAccess = units?.some((unit) => unit.visibility_level === 0 || unit.visibility_level === 2);

    // Redirect unauthorized users to home page once units are loaded
    useEffect(() => {
        if (unitsStatus === 'succeeded' && !hasDashboardAccess) {
            navigate('/home', { replace: true });
        }
    }, [unitsStatus, hasDashboardAccess, navigate]);
    
    // Local state for selected units - uses IDs from dashboardData (array for multi-select)
    const [selectedUnitIds, setSelectedUnitIds] = useState([]);

    const [openRows, setOpenRows] = useState({});
    const tooltipContainerRef = useRef(null);
    const tableContainerRef = useRef(null);
    const [sortOrder] = useState('asc'); // 'asc' or 'desc'
    const [sortedCheckpoints, setSortedCheckpoints] = useState([]);
    const [chartKey, setChartKey] = useState(0); // Add key to trigger chart re-render

    // Responsive chart dimensions
    const { isMobile, isTablet } = useResponsiveChart();

    // Store grouped checkpoints by type
    const [groupedCheckpoints, setGroupedCheckpoints] = useState({
        questions: [],
        measures: [],
        actionareas: []
    });

    // Map dashboard data to unit filter format
    const mapDashboardDataToUnits = (data) => {
        const allUnits = [];

        const processUnit = (unit, parentId = null, parentName = null, level = 0) => {
            const result = {
                id: String(unit.id),
                unit_name: unit.unitname,
                level: level,
                parent_unit: parentName,
                visibility_level: unit.visibility_level,
                children: []
            };

            allUnits.push(result);

            if (unit.children && unit.children.length > 0) {
                unit.children.forEach(child =>
                    processUnit(child, unit.id, unit.unitname, level + 1)
                );
            }

            return result;
        };

        data.forEach(unit => processUnit(unit));
        return allUnits;
    };

    // Memoize the filtered units data to avoid unnecessary recomputations
    // NOTE: This must be defined before any useEffect that depends on it
    const filteredUnits = useMemo(() => {
        return mapDashboardDataToUnits(dashboardData);
    }, [dashboardData]);

    useEffect(() => {
        document.title = 'TRAC - Dashboard';
    }, []);

    useEffect(() => {
        if (dashboardStatus === 'idle') {
            dispatch(fetchDashboardData());
        }
    }, [dashboardStatus, dispatch]);

    // Auto-select first unit when dashboard data is loaded
    useEffect(() => {
        if (dashboardStatus === 'succeeded' && dashboardData.length > 0 && selectedUnitIds.length === 0) {
            // Helper to find units recursively in dashboardData (which has proper children arrays)
            const findFirstEnabledNonLeafUnit = (units) => {
                for (const unit of units) {
                    // Check visibility (0 or 2 for dashboard) AND has children (not a leaf)
                    const hasValidVisibility = unit.visibility_level === 0 || unit.visibility_level === 2;
                    const hasChildren = unit.children && unit.children.length > 0;
                    
                    if (hasValidVisibility && hasChildren) {
                        return unit;
                    }
                    
                    // Search in children
                    if (unit.children && unit.children.length > 0) {
                        const found = findFirstEnabledNonLeafUnit(unit.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const firstEnabledUnit = findFirstEnabledNonLeafUnit(dashboardData);
            if (firstEnabledUnit) {
                setSelectedUnitIds([String(firstEnabledUnit.id)]);
                setChartKey(prevKey => prevKey + 1);
            }
        }
    }, [dashboardStatus, dashboardData, selectedUnitIds.length]);

    // Group checkpoints by type and sort them
    useEffect(() => {
        if (checkpoints && checkpoints.length > 0) {
            // Create a copy to sort
            const sorted = [...checkpoints].sort((a, b) => {
                const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            });
            setSortedCheckpoints(sorted);

            // Group by checkpoint types
            const grouped = sorted.reduce((acc, checkpoint) => {
                const type = checkpoint.checkpoint_type || 'measures';
                if (!acc[type]) {
                    acc[type] = [];
                }
                acc[type].push(checkpoint);
                return acc;
            }, {});

            setGroupedCheckpoints({
                questions: grouped.questions || [],
                measures: grouped.measures || [],
                actionareas: grouped.actionareas || []
            });
        } else {
            setSortedCheckpoints([]);
            setGroupedCheckpoints({
                questions: [],
                measures: [],
                actionareas: []
            });
        }
    }, [checkpoints, sortOrder]);

    const findUnitRecursiveInTree = (units, id) => {
        for (const u of units) {
            if (String(u.id) === String(id)) return u;
            if (u.children) {
                const found = findUnitRecursiveInTree(u.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const aggregateQuestionAndActionAreaResponses = (unit, checkpoints) => {
        // If no checkpoints or unit doesn't have children, return unit as is
        if (!checkpoints || !checkpoints.length || !unit.children || !unit.children.length) {
            return unit;
        }
    
        // Initialize a response object for each question checkpoint
        const questionResponses = {};
        const actionAreaResponses = {};
    
        // Initialize counts for each checkpoint
        checkpoints.forEach(checkpoint => {
            if (checkpoint.checkpoint_type === 'questions') {
                questionResponses[checkpoint.id] = {
                    yesCount: 0,
                    noCount: 0,
                    partialCount: 0, 
                    unknownCount: 0,
                    total: 0
                };
            }
    
            if (checkpoint.checkpoint_type === 'actionareas') {
                actionAreaResponses[checkpoint.id] = createActionAreaCheckpointCounts();
            }
        });
    
        const countUnitResponses = (units, depth = 0) => {
            units.forEach(childUnit => {
                // Only process units that have action boards (visibility_level 1 or 2)
                // Units with visibility_level 0 are dashboard-only and don't have action boards
                const hasActionBoard = childUnit.visibility_level === 1 || childUnit.visibility_level === 2;
                
                // Process this unit's statuses for each checkpoint (only if it has an action board)
                if (hasActionBoard) {
                checkpoints.forEach(checkpoint => {
                    if (checkpoint.checkpoint_type === 'questions' && checkpoint.statuses) {
                        // Process question statuses - only count statuses that belong to this specific unit
                        const matchingStatuses = checkpoint.statuses.filter(status => {
                            // Only include statuses that belong to this specific unit
                            return status.question &&
                                status.unit &&
                                String(status.unit) === String(childUnit.id);
                        });
    
                        if (matchingStatuses.length > 0) {
                            // For each unit, we should only find one status per question checkpoint
                            // (one unit can only answer a question once)
                            matchingStatuses.forEach(status => {
                                const statusValue = status.status?.toLowerCase();
    
                                // Only count the status once - this is for this specific unit
                                if (statusValue === 'ja') {
                                    questionResponses[checkpoint.id].yesCount++;
                                } else if (statusValue === 'nein') {
                                    questionResponses[checkpoint.id].noCount++;
                                } else if (statusValue === 'teilweise') {
                                    questionResponses[checkpoint.id].partialCount++;
                                } else {
                                    // Count null or any other undefined status as "unknown"
                                    questionResponses[checkpoint.id].unknownCount++;
                                }
                                // Only increment total for valid units (one per unit)
                                questionResponses[checkpoint.id].total++;
                            });
                        }
                    }

                    // Process action area statuses from checkpoint data
                    if (checkpoint.checkpoint_type === 'actionareas' && checkpoint.statuses) {
                        const matchingStatuses = checkpoint.statuses.filter(status => {
                            // Ensure we have proper unit matching here
                            const unitMatches = status.unit && status.actionarea &&
                                (String(status.unit) === String(childUnit.id) ||
                                    (status.unit.id && String(status.unit.id) === String(childUnit.id)));

                            return unitMatches && status.actionarea;
                        });

                        if (matchingStatuses.length > 0) {
                            matchingStatuses.forEach(status => {
                                addActionAreaCheckpointStatusCount(
                                    actionAreaResponses[checkpoint.id],
                                    status.status
                                );
                            });
                        }
                    }
                });

                // Also add the unit's action area data from API
                // This is important for units that don't have explicit statuses in checkpoints
                if (childUnit.actionAreas > 0) {
                    checkpoints.forEach(checkpoint => {
                        if (checkpoint.checkpoint_type === 'actionareas') {
                            // If we don't have explicit statuses for this unit/checkpoint combination,
                            // use the API data to add to our aggregation
                            const hasExplicitStatuses = checkpoint.statuses?.some(status =>
                                (status.unit?.id === childUnit.id || status.unit === childUnit.id) && status.actionarea
                            );

                            if (!hasExplicitStatuses) {
                                actionAreaResponses[checkpoint.id].noStatus += childUnit.actionAreas;
                                actionAreaResponses[checkpoint.id].total += childUnit.actionAreas;
                            }
                        }
                    });
                }
                } // End of hasActionBoard check

                // Recursively process children (regardless of this unit's visibility)
                if (childUnit.children && childUnit.children.length > 0) {
                    countUnitResponses(childUnit.children, depth + 1);
                }
            });
        };

        // Start counting from children of current unit
        countUnitResponses(unit.children);

        // IMPORTANT: Also count the parent unit's OWN responses (not just children)
        // Only if the parent unit has an action board (visibility_level 1 or 2)
        const parentHasActionBoard = unit.visibility_level === 1 || unit.visibility_level === 2;
        
        if (parentHasActionBoard) {
        checkpoints.forEach(checkpoint => {
            if (checkpoint.checkpoint_type === 'questions' && checkpoint.statuses) {
                // Check if the parent unit itself has a response to this question
                const parentStatus = checkpoint.statuses.find(status => {
                    return status.question &&
                        status.unit &&
                        (String(status.unit) === String(unit.id) || 
                         (status.unit.id && String(status.unit.id) === String(unit.id)));
                });

                if (parentStatus) {
                    const statusValue = parentStatus.status?.toLowerCase();
                    if (statusValue === 'ja') {
                        questionResponses[checkpoint.id].yesCount++;
                    } else if (statusValue === 'nein') {
                        questionResponses[checkpoint.id].noCount++;
                    } else if (statusValue === 'teilweise') {
                        questionResponses[checkpoint.id].partialCount++;
                    } else {
                        questionResponses[checkpoint.id].unknownCount++;
                    }
                    questionResponses[checkpoint.id].total++;
                }
            }

            if (checkpoint.checkpoint_type === 'actionareas' && checkpoint.statuses) {
                // Check if the parent unit itself has action area statuses
                const parentActionAreaStatuses = checkpoint.statuses.filter(status => {
                    const unitMatches = status.unit && status.actionarea &&
                        (String(status.unit) === String(unit.id) ||
                            (status.unit.id && String(status.unit.id) === String(unit.id)));
                    return unitMatches && status.actionarea;
                });

                if (parentActionAreaStatuses.length > 0) {
                    parentActionAreaStatuses.forEach(status => {
                        addActionAreaCheckpointStatusCount(
                            actionAreaResponses[checkpoint.id],
                            status.status
                        );
                    });
                }
            }
        });

        // Also check if the parent unit has its own action areas (from API data)
        if (unit.actionAreas > 0) {
            checkpoints.forEach(checkpoint => {
                if (checkpoint.checkpoint_type === 'actionareas') {
                    // Check if we already counted explicit statuses for this parent unit
                    const hasExplicitStatuses = checkpoint.statuses?.some(status =>
                        (status.unit?.id === unit.id || status.unit === unit.id) && status.actionarea
                    );

                    if (!hasExplicitStatuses) {
                        actionAreaResponses[checkpoint.id].noStatus += unit.actionAreas;
                        actionAreaResponses[checkpoint.id].total += unit.actionAreas;
                    }
                }
            });
        }
        } // End of parentHasActionBoard check

        // Add responses to the unit
        return {
            //...unit,
            questionResponses,
            actionAreaResponses
        };
    };

    // eslint-disable-next-line no-unused-vars
    const aggregateMeasureResponsesLegacy = (parentUnit, allMeasureCheckpoints, fullDashboardData) => {
        const aggregatedResponses = {};
    
        if (!parentUnit.children || parentUnit.children.length === 0 || !allMeasureCheckpoints || allMeasureCheckpoints.length === 0) {
            return aggregatedResponses;
        }

        // Helper function to count statuses for a single unit from checkpoint data
        const countUnitStatuses = (unitNode, checkpoint, aggregates) => {
            if (!checkpoint.statuses || checkpoint.statuses.length === 0) return;
            
            const unitStatuses = checkpoint.statuses.filter(status => 
                String(status.unit) === String(unitNode.id) ||
                (status.unit?.id && String(status.unit.id) === String(unitNode.id))
            );
            
            unitStatuses.forEach(status => {
                const statusValue = status.status?.toLowerCase() || '';
                aggregates.totalMeasures++;
                
                switch (statusValue) {
                    case 'gestoppt':
                    case 'schwierigkeiten':
                        aggregates.stoppedCount++;
                        break;
                    case 'nicht begonnen':
                    case '': // Empty status defaults to not started
                        aggregates.notStartedCount++;
                        break;
                    case 'in bearbeitung - verzögert':
                    case 'verzögert':
                        aggregates.delayedProgressCount++;
                        break;
                    case 'in bearbeitung - nach plan':
                    case 'planmäßig':
                        aggregates.onTrackProgressCount++;
                        break;
                    case 'abgeschlossen':
                        aggregates.completedCount++;
                        break;
                    default:
                        aggregates.notStartedCount++;
                        break;
                }
            });
        };
    
        allMeasureCheckpoints.forEach(checkpoint => {
            const checkpointAggregates = {
                stoppedCount: 0,
                notStartedCount: 0,
                delayedProgressCount: 0,
                onTrackProgressCount: 0,
                completedCount: 0,
                totalMeasures: 0,
            };
    
            const collectUnitData = (currentUnitNode) => {
                // Only process units that have action boards (visibility_level 1 or 2)
                const hasActionBoard = currentUnitNode.visibility_level === 1 || currentUnitNode.visibility_level === 2;
                
                // Count this unit's OWN statuses if it has an action board
                // (regardless of whether it has children or not)
                if (hasActionBoard) {
                    countUnitStatuses(currentUnitNode, checkpoint, checkpointAggregates);
                }
                
                // Also recursively process children (regardless of this unit's visibility)
                if (currentUnitNode.children && currentUnitNode.children.length > 0) {
                    currentUnitNode.children.forEach(childRef => {
                        // Find the full child object from the main dashboardData tree
                        const fullChildUnit = findUnitRecursiveInTree(fullDashboardData, childRef.id);
                        if (fullChildUnit) {
                            collectUnitData(fullChildUnit);
                        }
                    });
                }
            };
    
            // Start collection for the descendants of parentUnit
            parentUnit.children.forEach(childRef => {
                const fullChildUnit = findUnitRecursiveInTree(fullDashboardData, childRef.id);
                if (fullChildUnit) {
                    collectUnitData(fullChildUnit);
                }
            });

            // IMPORTANT: Also include the parent unit's OWN measures if it has an action board
            const parentHasActionBoard = parentUnit.visibility_level === 1 || parentUnit.visibility_level === 2;
            if (parentHasActionBoard) {
                countUnitStatuses(parentUnit, checkpoint, checkpointAggregates);
            }
    
            // Store aggregated data for this checkpoint
            aggregatedResponses[checkpoint.id] = checkpointAggregates;
        });
        
        return aggregatedResponses;
    };
    const aggregateMeasureResponsesForDashboard = (parentUnit, allMeasureCheckpoints, fullDashboardData) => {
        const aggregatedResponses = {};

        if (!parentUnit.children || parentUnit.children.length === 0 || !allMeasureCheckpoints || allMeasureCheckpoints.length === 0) {
            return aggregatedResponses;
        }

        const countUnitStatuses = (unitNode, checkpoint, aggregates) => {
            if (!checkpoint.statuses || checkpoint.statuses.length === 0) return;

            const unitStatuses = checkpoint.statuses.filter(status =>
                String(status.unit) === String(unitNode.id) ||
                (status.unit?.id && String(status.unit.id) === String(unitNode.id))
            );

            unitStatuses.forEach(status => {
                addMeasureCheckpointStatusCount(aggregates, status.status);
            });
        };

        allMeasureCheckpoints.forEach(checkpoint => {
            const checkpointAggregates = createMeasureCheckpointCounts();

            const collectUnitData = (currentUnitNode) => {
                const hasActionBoard = currentUnitNode.visibility_level === 1 || currentUnitNode.visibility_level === 2;

                if (hasActionBoard) {
                    countUnitStatuses(currentUnitNode, checkpoint, checkpointAggregates);
                }

                if (currentUnitNode.children && currentUnitNode.children.length > 0) {
                    currentUnitNode.children.forEach(childRef => {
                        const fullChildUnit = findUnitRecursiveInTree(fullDashboardData, childRef.id);
                        if (fullChildUnit) {
                            collectUnitData(fullChildUnit);
                        }
                    });
                }
            };

            parentUnit.children.forEach(childRef => {
                const fullChildUnit = findUnitRecursiveInTree(fullDashboardData, childRef.id);
                if (fullChildUnit) {
                    collectUnitData(fullChildUnit);
                }
            });

            const parentHasActionBoard = parentUnit.visibility_level === 1 || parentUnit.visibility_level === 2;
            if (parentHasActionBoard) {
                countUnitStatuses(parentUnit, checkpoint, checkpointAggregates);
            }

            aggregatedResponses[checkpoint.id] = checkpointAggregates;
        });

        return aggregatedResponses;
    };

    // Enhance question checkpoints with full question text from default questions
    const enhancedQuestionCheckpoints = groupedCheckpoints.questions.map(checkpoint => {
        const enhancedCheckpoint = { ...checkpoint };

        // Enhance the statuses with more detailed question information
        if (enhancedCheckpoint.statuses) {
            enhancedCheckpoint.statuses = enhancedCheckpoint.statuses.map(status => {
                if (status.question) {
                    const matchingDefaultQuestion = defaultQuestions.find(q => q.id === status.question.id);
                    if (matchingDefaultQuestion) {
                        return {
                            ...status,
                            question: {
                                ...status.question,
                                question_tag: matchingDefaultQuestion.question_tag,
                                question_text: matchingDefaultQuestion.question_text || status.question.text
                            }
                        };
                    }
                }
                return status;
            });
        }
        return enhancedCheckpoint;
    });

    // Create enhanced sorted checkpoints (date-ordered with enhanced question data)
    const enhancedSortedCheckpoints = sortedCheckpoints.map(checkpoint => {
        if (checkpoint.checkpoint_type === 'questions') {
            // Find the matching enhanced question checkpoint
            const enhanced = enhancedQuestionCheckpoints.find(eq => eq.id === checkpoint.id);
            return enhanced || checkpoint;
        }
        return checkpoint;
    });

    const handleToggleOpen = (unitId) => {
        setOpenRows((prev) => ({
            ...prev,
            [unitId]: !prev[unitId],
        }));
    };

    // Handle unit selection from filter (multi-select)
    const handleSelectUnits = (unitIds) => {
        setSelectedUnitIds(unitIds);
        setChartKey(prevKey => prevKey + 1);
    };

    // Format checkpoint date
    const formatCheckpointDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE');
    };

    // Get filtered data based on selected units (supports multiple selection)
    const getFilteredData = useCallback(() => {
        if (!selectedUnitIds || selectedUnitIds.length === 0 || !dashboardData.length) return dashboardData;

        // Find a unit by id recursively
        const findUnit = (units, id) => {
            for (const unit of units) {
                if (String(unit.id) === String(id)) return unit;
                if (unit.children && unit.children.length > 0) {
                    const found = findUnit(unit.children, id);
                    if (found) return found;
                }
            }
            return null;
        };

        // Collect all selected units
        const selectedUnits = [];
        selectedUnitIds.forEach(id => {
            const unit = findUnit(dashboardData, id);
            if (unit) {
                selectedUnits.push(unit);
            }
        });

        return selectedUnits.length > 0 ? selectedUnits : dashboardData;
    }, [selectedUnitIds, dashboardData]);

    // Use useMemo to memoize filtered data to prevent unnecessary recalculations
    const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

    // Recursive function to render rows - note we don't use filteredData here
    // to keep all rows in the table
    const renderRows = (units, level = 0) => {
        return units.flatMap((unit) => {
            // Add aggregated question and action area responses to units with children
            let enhancedUnit = { ...unit }; // Clone unit to avoid direct mutation

            if (unit.children && unit.children.length > 0) {
                const aggregatedQuestionActionArea = aggregateQuestionAndActionAreaResponses(unit, sortedCheckpoints);
                const aggregatedMeasures = aggregateMeasureResponsesForDashboard(unit, groupedCheckpoints.measures, dashboardData);

                enhancedUnit = {
                    ...enhancedUnit,
                    questionResponses: aggregatedQuestionActionArea.questionResponses,
                    actionAreaResponses: aggregatedQuestionActionArea.actionAreaResponses,
                    measureCheckpointResponses: aggregatedMeasures
                };
            }


            return (
                <React.Fragment key={unit.id}>
                    <ExpandableDashboardRow
                        row={enhancedUnit}
                        toggleOpen={() => handleToggleOpen(unit.id)}
                        isOpen={!!openRows[unit.id]}
                        tooltipContainerRef={tooltipContainerRef}
                        hasChildren={unit.children && unit.children.length > 0}
                        level={level}
                        groupedCheckpoints={{
                            ...groupedCheckpoints,
                            questions: enhancedQuestionCheckpoints
                        }}
                        sortedCheckpoints={enhancedSortedCheckpoints}
                    />
                    {unit.children && unit.children.length > 0 && openRows[unit.id] && renderRows(unit.children, level + 1)}
                </React.Fragment>
            );
        });
    };

    // Calculate totals - this uses filteredData to update when unit changes
    const calculateTotals = (data) => {
        // For root level units, simply sum their values (which already include child values)
        return data.reduce(
            (totals, unit) => {
                totals.actionBoards += unit.actionBoards || 0;
                totals.actionAreas += unit.actionAreas || 0;
                totals.measures += unit.measures || 0;
                return totals;
            },
            { actionBoards: 0, actionAreas: 0, measures: 0 }
        );
    };

    // Calculate tag distribution for pie chart - uses filteredData to update when unit changes
    // NOTE: We don't process children recursively because each unit's tags already include
    // aggregated tags from all descendants (computed in dashboardSlice)
    const calculateTagDistribution = (data) => {
        const tagCounts = {};

        // Only process the top-level filtered units - their tags already include children's tags
        if (Array.isArray(data)) {
            data.forEach(unit => {
                if (unit.tags && Array.isArray(unit.tags)) {
                    unit.tags.forEach(tag => {
                        if (tag.tag_name) {  // Only process non-null tags
                            const tagName = tag.tag_name || 'Unkategorisiert';
                            tagCounts[tagName] = (tagCounts[tagName] || 0) + (tag.count || 1);
                        }
                    });
                }
            });
        }

        // Convert to array for the pie chart and sort by largest value
        return Object.entries(tagCounts)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    };

    // Memoize totals and tag distribution calculations
    const totals = useMemo(() => calculateTotals(filteredData), [filteredData]);
    const tagDistribution = useMemo(() => calculateTagDistribution(filteredData), [filteredData]);

    // Pie chart colors - sophisticated palette matching Figma design
    const COLORS = ['#5B6B73', '#879298', '#A3AEB3', '#C4CDD1', '#3D4F56', '#6B7D85', '#98A8AF'];

    // Calculate column widths based on the number of checkpoints
    const unitColumnWidth = 300;
    const fixedColumnWidth = 200;

    // Function to get a better display name for question checkpoint headers
    const getQuestionDisplayName = (checkpoint) => {
        // If there are statuses with questions, use the first question's tag
        if (checkpoint.statuses && checkpoint.statuses.length > 0) {
            const firstStatusWithQuestion = checkpoint.statuses.find(s => s.question);
            if (firstStatusWithQuestion && firstStatusWithQuestion.question) {
                return firstStatusWithQuestion.question.question_tag || checkpoint.checkpoint_name;
            }
        }

        // Fallback to checkpoint name
        return checkpoint.checkpoint_name;
    };

    const userName = useMemo(() => {
        if (!userData) return '';
        return userData.first_name || userData.username || '';
    }, [userData]);

    // Don't render dashboard content while checking access or if access is denied
    if (unitsStatus === 'loading' || unitsStatus === 'idle') {
        return (
            <div className="dashboard-page flex-grow-1">
                <div className="dashboard-loading">
                    <Spinner animation="border" role="status" className="mb-4">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </div>
        );
    }

    // If units loaded but user has no access, don't render (redirect will happen via useEffect)
    if (unitsStatus === 'succeeded' && !hasDashboardAccess) {
        return null;
    }

    return (
        <div className="dashboard-page flex-grow-1">
            <div className="dashboard-header">
                <h2 className="dashboard-title">
                    Hallo {userName}, hier ist Dein Dashboard!
                </h2>
                <p className="dashboard-subtitle">
                    Behalte hier gebündelt den Prozessstand in Deinem Bereich im Blick.
                </p>
            </div>

            <hr style={{ marginBottom: '24px' }} />

            {/* Status Handling */}
            {dashboardStatus === 'loading' && (
                <div className="dashboard-loading">
                    <Spinner animation="border" role="status" className="mb-4">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}

            {dashboardStatus === 'failed' && (
                <Alert variant="danger" className="my-4">
                    {dashboardError || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.'}
                </Alert>
            )}

            {dashboardStatus === 'succeeded' && (
                <>
                    {/* Unit Filter - Aligned left with limited width */}
                    <div className="dashboard-filter-section">
                        <div style={{ maxWidth: '622px' }}>
                            <UnitFilter
                                units={filteredUnits}
                                selectedUnitIds={selectedUnitIds}
                                onSelectUnits={handleSelectUnits}
                                visibilityMode="dashboard"
                                singleSelect={true}
                                disableLeafUnits={true}
                                deferUpdateOnClose={true}
                            />
                        </div>
                    </div>

                    {/* Statistics Section - Large numbers with labels */}
                    <div className="dashboard-stats-section">
                        <div className="dashboard-stat-item">
                            <span className="dashboard-stat-number">
                                {String(totals.actionBoards).padStart(2, '0')}
                            </span>
                            <span className="dashboard-stat-label">Actionboards</span>
                        </div>
                        <div className="dashboard-stat-item">
                            <span className="dashboard-stat-number">
                                {String(totals.actionAreas).padStart(2, '0')}
                            </span>
                            <span className="dashboard-stat-label">Handlungsfelder</span>
                        </div>
                        <div className="dashboard-stat-item">
                            <span className="dashboard-stat-number">
                                {String(totals.measures).padStart(2, '0')}
                            </span>
                            <span className="dashboard-stat-label">Maßnahmen</span>
                        </div>
                    </div>

                    <hr />

                    {/* Tags Distribution Pie Chart - Updates based on filtered data */}
                    <div className="dashboard-chart-section">
                        {tagDistribution && tagDistribution.length > 0 ? (
                            <>
                                {/* Mobile Layout: Chart + Legend below */}
                                {isMobile ? (
                                    <div 
                                        className="dashboard-chart-container is-mobile"
                                        key={`chart-mobile-${chartKey}`}
                                    >
                                        <div className="dashboard-chart-wrapper">
                                            <ResponsiveContainer width="100%" height={280}>
                                                <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                                                    <Pie
                                                        data={tagDistribution}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={50}
                                                        outerRadius={90}
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                        nameKey="name"
                                                        isAnimationActive={true}
                                                        animationDuration={800}
                                                        label={false}
                                                        labelLine={false}
                                                    >
                                                        {tagDistribution.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-mobile-${index}`}
                                                                fill={COLORS[index % COLORS.length]}
                                                            />
                                                        ))}
                                                    </Pie>
                                                    {/* Center text - smaller for mobile */}
                                                    <text
                                                        x="50%"
                                                        y="50%"
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        style={{
                                                            fontFamily: 'Gilroy, sans-serif',
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            fill: '#11131C'
                                                        }}
                                                    >
                                                        Themen
                                                    </text>
                                                    <RechartsTooltip
                                                        formatter={(value, name, props) => [
                                                            `${value} ${value === 1 ? 'Handlungsfeld' : 'Handlungsfelder'}`,
                                                            props.payload.name
                                                        ]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Custom Legend for Mobile - rendered outside the chart */}
                                        <ul className="dashboard-chart-legend">
                                            {tagDistribution.map((item, index) => {
                                                const total = tagDistribution.reduce((sum, t) => sum + t.value, 0);
                                                const percent = Math.round((item.value / total) * 100);
                                                return (
                                                    <li key={item.name} className="dashboard-chart-legend-item">
                                                        <span 
                                                            className="dashboard-chart-legend-swatch"
                                                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                        />
                                                        <span className="dashboard-chart-legend-name">{item.name}</span>
                                                        <span className="dashboard-chart-legend-value">{percent}%</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ) : (
                                    /* Desktop/Tablet Layout: Inline labels around the chart */
                                    <div 
                                        className={`dashboard-chart-container ${isTablet ? 'is-tablet' : ''}`}
                                        key={`chart-desktop-${chartKey}`}
                                    >
                                        <div className="dashboard-chart-wrapper">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart
                                                    margin={isTablet
                                                        ? { top: 60, right: 100, bottom: 60, left: 100 }
                                                        : { top: 80, right: 120, bottom: 80, left: 120 }
                                                    }
                                                >
                                                    <Pie
                                                        data={tagDistribution}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius="38%"
                                                        outerRadius="65%"
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                        nameKey="name"
                                                        isAnimationActive={true}
                                                        animationDuration={1500}
                                                        labelLine={({ cx, cy, midAngle, outerRadius }) => {
                                                            const RADIAN = Math.PI / 180;
                                                            const lineLength = isTablet ? 20 : 35;
                                                            const startRadius = outerRadius + 6;
                                                            const endRadius = outerRadius + lineLength;
                                                            
                                                            const x1 = cx + startRadius * Math.cos(-midAngle * RADIAN);
                                                            const y1 = cy + startRadius * Math.sin(-midAngle * RADIAN);
                                                            const x2 = cx + endRadius * Math.cos(-midAngle * RADIAN);
                                                            const y2 = cy + endRadius * Math.sin(-midAngle * RADIAN);
                                                            
                                                            return (
                                                                <path
                                                                    d={`M${x1},${y1}L${x2},${y2}`}
                                                                    stroke="#A6AEB2"
                                                                    strokeWidth={2}
                                                                    fill="none"
                                                                />
                                                            );
                                                        }}
                                                        label={({ name, percent, cx, cy, midAngle, outerRadius }) => {
                                                            const percentValue = Math.round(percent * 100);
                                                            const RADIAN = Math.PI / 180;
                                                            const labelOffset = isTablet ? 30 : 45;
                                                            const labelRadius = outerRadius + labelOffset;
                                                            const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
                                                            const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);
                                                            const textAnchor = x > cx ? 'start' : 'end';
                                                            const fontSize = isTablet ? '14px' : '18px';
                                                            const percentFontSize = isTablet ? '12px' : '16px';
                                                            
                                                            return (
                                                                <g>
                                                                    <text
                                                                        x={x}
                                                                        y={y - 8}
                                                                        textAnchor={textAnchor}
                                                                        style={{
                                                                            fontFamily: 'Gilroy, sans-serif',
                                                                            fontWeight: 700,
                                                                            fontSize: fontSize,
                                                                            fill: '#11131C'
                                                                        }}
                                                                    >
                                                                        {name}
                                                                    </text>
                                                                    <text
                                                                        x={x}
                                                                        y={y + 12}
                                                                        textAnchor={textAnchor}
                                                                        style={{
                                                                            fontFamily: 'Gilroy, sans-serif',
                                                                            fontWeight: 700,
                                                                            fontSize: percentFontSize,
                                                                            fill: '#11131C',
                                                                            opacity: 0.8
                                                                        }}
                                                                    >
                                                                        {percentValue}%
                                                                    </text>
                                                                </g>
                                                            );
                                                        }}
                                                    >
                                                        {tagDistribution.map((entry, index) => (
                                                            <Cell
                                                                key={`cell-desktop-${index}`}
                                                                fill={COLORS[index % COLORS.length]}
                                                            />
                                                        ))}
                                                    </Pie>
                                                    {/* Center text */}
                                                    <text
                                                        x="50%"
                                                        y="50%"
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        style={{
                                                            fontFamily: 'Gilroy, sans-serif',
                                                            fontSize: '22px',
                                                            fontWeight: 'bold',
                                                            fill: '#11131C'
                                                        }}
                                                    >
                                                        Themen
                                                    </text>
                                                    <RechartsTooltip
                                                        formatter={(value, name, props) => [
                                                            `${value} ${value === 1 ? 'Handlungsfeld' : 'Handlungsfelder'}`,
                                                            props.payload.name
                                                        ]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="dashboard-chart-container no-data">
                                <div className="text-center">Keine Themendaten verfügbar</div>
                            </div>
                        )}
                    </div>

                    {/* Table - Does NOT change when filtering units */}
                    <div
                        className="dashboard-table-container"
                        ref={tableContainerRef}
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            position: 'relative'
                        }}
                    >
                        <div
                            style={{
                                minWidth: unitColumnWidth +
                                    (fixedColumnWidth * sortedCheckpoints.length),
                                width: 'auto',
                                margin: '0 auto', // Center the table
                                maxWidth: 'fit-content' // Prevent excessive width
                            }}
                        >
                            <Table bordered hover className="dashboard-table">
                                <colgroup>
                                    <col style={{ width: unitColumnWidth, minWidth: unitColumnWidth }} />

                                    {/* All Checkpoints - ordered by date */}
                                    {sortedCheckpoints.map((checkpoint) => (
                                        <col
                                            key={`col-${checkpoint.checkpoint_type}-${checkpoint.id}`}
                                            style={{ width: fixedColumnWidth, minWidth: fixedColumnWidth }}
                                        />
                                    ))}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th
                                            rowSpan="1"
                                            className="position-sticky bg-white"
                                            style={{
                                                left: 0,
                                                zIndex: 10,
                                                boxShadow: '5px 0 5px -5px rgba(0,0,0,.1)'
                                            }}
                                        >
                                            Einheit
                                        </th>

                                        {/* All checkpoint headers - ordered by date */}
                                        {sortedCheckpoints.map((checkpoint) => {
                                            const checkpointType = checkpoint.checkpoint_type || 'measures';
                                            
                                            if (checkpointType === 'questions') {
                                                return (
                                                    <th
                                                        key={checkpoint.id}
                                                        rowSpan="1"
                                                        className="text-center question-column-header"
                                                        title={checkpoint.statuses?.[0]?.question?.text || checkpoint.checkpoint_name}
                                                        style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                                                    >
                                                        {getQuestionDisplayName(checkpoint)}
                                                        <div className="small text-muted">
                                                            bis {formatCheckpointDate(checkpoint.end_date)}
                                                        </div>
                                                    </th>
                                                );
                                            } else if (checkpointType === 'actionareas') {
                                                return (
                                                    <th
                                                        key={checkpoint.id}
                                                        rowSpan="1"
                                                        className="text-center actionarea-column-header"
                                                        style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                                                    >
                                                        Zielerreichung
                                                        {checkpoint.end_date && (
                                                            <div className="small text-muted">
                                                                bis {formatCheckpointDate(checkpoint.end_date)}
                                                            </div>
                                                        )}
                                                    </th>
                                                );
                                            } else {
                                                // Default to measures
                                                return (
                                                    <th
                                                        key={checkpoint.id}
                                                        className="text-center checkpoint-header"
                                                        style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                                                    >
                                                        Umsetzungsstand
                                                        {checkpoint.end_date && (
                                                            <div className="small text-muted">
                                                                bis {formatCheckpointDate(checkpoint.end_date)}
                                                            </div>
                                                        )}
                                                    </th>
                                                );
                                            }
                                        })}
                                    </tr>
                                </thead>
                                <tbody>{renderRows(dashboardData)}</tbody>
                            </Table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
